<template>
  <div class="container my-5 p-lg-5 trusted">
    <section aria-labelledby="trusted-title">
      <h2
        id="trusted-title"
        class="font-weight-bold text-center dark-grey-text pb-2"
      >
        Ils nous ont fait confiance
      </h2>
      <hr class="w-header my-4" />
      <p class="lead text-center text-muted pt-2 mb-5">
        Des projets numériques réalisés pour des entreprises et associations aux
        univers variés
      </p>

      <div class="trusted__grid">
        <component
          :is="partner.url ? 'a' : 'div'"
          v-for="partner in partners"
          :key="partner.name"
          class="trusted__partner"
          :class="{ 'trusted__partner--dark': partner.darkVisual }"
          :href="partner.url || undefined"
          :target="partner.url ? '_blank' : undefined"
          :rel="partner.url ? 'noopener noreferrer' : undefined"
          :aria-label="
            partner.url
              ? `Découvrir ${partner.name} (nouvel onglet)`
              : undefined
          "
        >
          <div
            class="trusted__logo"
            :style="{ '--logo-scale': partner.scale || 1 }"
          >
            <img
              :src="partner.logo"
              :alt="`Logo ${partner.name}`"
              loading="lazy"
              decoding="async"
            />
          </div>

          <div class="trusted__name">
            <span>{{ partner.name }}</span>
            <i
              v-if="partner.url"
              class="fas fa-external-link-alt"
              aria-hidden="true"
            ></i>
          </div>
        </component>
      </div>
    </section>
  </div>
</template>

<script>
import alprintLogo from "@/assets/partners/alprint.webp";
import biagginiLogo from "@/assets/partners/biaggini.webp";
import chezMoussLogo from "@/assets/partners/chez-mouss.webp";
import daadiLogo from "@/assets/partners/daadi.webp";
import ittiLogo from "@/assets/partners/itti.webp";
import livratosLogo from "@/assets/partners/livratos.webp";
import nubetecLogo from "@/assets/partners/nubetec.webp";
import qaszarLogo from "@/assets/partners/qaszar.webp";
import teamLogo from "@/assets/partners/team.webp";
import yclaLogo from "@/assets/partners/ycla.webp";
import { artigoneLogo, cocoriporteLogo } from "@/assets/partners/officialLogos";

export default {
  name: "AboutTrusted",
  data() {
    return {
      partners: [
        {
          name: "Artigone.fr",
          url: "https://artigone.fr",
          logo: artigoneLogo,
        },
        {
          name: "Cocoriporte",
          url: "https://cocoriporte.fr",
          logo: cocoriporteLogo,
          scale: 1.35,
        },
        {
          name: "You Can Learn",
          url: "https://youcanlearn.fr",
          logo: yclaLogo,
          darkVisual: true,
        },
        {
          name: "Livratos.app",
          url: "https://livratos.app",
          logo: livratosLogo,
        },
        {
          name: "Livratos.fr",
          url: "https://livratos.fr",
          logo: livratosLogo,
        },
        {
          name: "Qaszar",
          url: "https://qaszar.app",
          logo: qaszarLogo,
          darkVisual: true,
        },
        {
          name: "Institut Taxi",
          url: "https://institut-taxi.fr",
          logo: ittiLogo,
          scale: 1.35,
        },
        {
          name: "Nubetec",
          url: "https://nubetec.fr",
          logo: nubetecLogo,
        },
        {
          name: "Team",
          url: "https://asso-team.fr",
          logo: teamLogo,
        },
        {
          name: "Atelier Al Print",
          url: "https://atelieralprint.fr",
          logo: alprintLogo,
          scale: 1.15,
        },
        { name: "Daadi Kitchen", logo: daadiLogo, scale: 1.55 },
        { name: "Chez Mouss", logo: chezMoussLogo, scale: 1.25 },
        { name: "Biaggini Électricité", logo: biagginiLogo, scale: 1.55 },
      ],
    };
  },
};
</script>

<style scoped lang="scss">
.trusted {
  padding-top: 3rem;
  padding-bottom: 3rem;

  &__grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 1rem;
    padding: 0 1rem;
  }

  &__partner {
    display: flex;
    min-width: 0;
    min-height: 155px;
    flex-direction: column;
    overflow: hidden;
    border: 1px solid #e5e5e5;
    border-radius: 5px;
    color: $color-darker;
    background: #fff;
    text-decoration: none;
    transition: border-color 180ms ease, box-shadow 180ms ease,
      transform 180ms ease;

    &:is(a):hover,
    &:is(a):focus-visible {
      border-color: $color-primary;
      color: $color-darker;
      box-shadow: 0 6px 18px rgba(31, 30, 30, 0.1);
      outline: none;
      text-decoration: none;
      transform: translateY(-3px);

      .trusted__name i {
        color: $color-primary;
      }
    }

    &--dark .trusted__logo {
      background: $color-darker;
    }
  }

  &__logo {
    display: flex;
    min-height: 108px;
    flex: 1;
    align-items: center;
    justify-content: center;
    padding: 1.2rem;
    background: #fff;

    img {
      width: 150px;
      height: 68px;
      object-fit: contain;
      transform: scale(var(--logo-scale));
      transform-origin: center;
    }
  }

  &__name {
    display: flex;
    min-height: 46px;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.7rem 0.85rem;
    border-top: 1px solid #eee;
    font-size: 0.78rem;
    font-weight: 700;

    i {
      color: #aaa;
      font-size: 0.68rem;
      transition: color 180ms ease;
    }
  }
}

@media (max-width: 991.98px) {
  .trusted__grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 767.98px) {
  .trusted__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 575.98px) {
  .trusted {
    padding: 2.5rem 0.75rem;

    &__grid {
      grid-template-columns: 1fr;
      padding: 0;
    }

    &__partner {
      min-height: 140px;
    }

    &__logo {
      min-height: 96px;
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .trusted__partner,
  .trusted__name i {
    transition: none;
  }
}
</style>
